/**
 * The shape room: toys about space, symmetry and pattern. They all reward
 * fiddling rather than answering, which is why they live apart from the number
 * toys — a child in here is making something, not working something out.
 */

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { AlignJustify, FlipHorizontal2, Puzzle, Scale, Scissors, Shapes } from 'lucide-preact';
import { Chip } from '../ui/controls.tsx';
import type { Scene } from '../lib/scene.ts';
import { MirrorToy } from './math/MirrorToy.tsx';
import { ShapeToy } from './math/ShapeToy.tsx';
import { FractionToy } from './math/FractionToy.tsx';
import { BalanceToy } from './math/BalanceToy.tsx';
import { WallToy } from './math/WallToy.tsx';
import { PuzzleToy } from './math/PuzzleToy.tsx';

type ToyId = 'shapes' | 'mirror' | 'cut' | 'wall' | 'balance' | 'puzzle';

const TOYS: Array<{ id: ToyId; label: string; icon: JSX.Element }> = [
  { id: 'shapes', label: 'Shapes', icon: <Shapes size={20} strokeWidth={2.75} /> },
  { id: 'mirror', label: 'Mirror', icon: <FlipHorizontal2 size={20} strokeWidth={2.75} /> },
  { id: 'cut', label: 'Cut', icon: <Scissors size={20} strokeWidth={2.75} /> },
  { id: 'wall', label: 'Wall', icon: <AlignJustify size={20} strokeWidth={2.75} /> },
  { id: 'balance', label: 'Balance', icon: <Scale size={20} strokeWidth={2.75} /> },
  { id: 'puzzle', label: 'Puzzle', icon: <Puzzle size={20} strokeWidth={2.75} /> },
];

interface Props {
  onExit: () => void;
  onSaved: () => void;
  initial?: Scene | null;
}

export function ShapeLab({ onExit, onSaved, initial }: Props): JSX.Element {
  // Reopening a saved kaleidoscope lands straight on the toy that made it.
  const [toy, setToy] = useState<ToyId>(initial?.kind === 'mirror' ? 'mirror' : 'shapes');

  const tabs = (
    <div class="dock__row dock__row--tabs" role="group" aria-label="Shape toys">
      {TOYS.map((t) => (
        <Chip key={t.id} label={t.label} pressed={toy === t.id} onClick={() => setToy(t.id)}>
          {t.icon}
        </Chip>
      ))}
    </div>
  );

  switch (toy) {
    case 'shapes':
      return <ShapeToy onExit={onExit} tabs={tabs} />;
    case 'mirror':
      return <MirrorToy onExit={onExit} onSaved={onSaved} tabs={tabs} initial={initial} />;
    case 'cut':
      return <FractionToy onExit={onExit} tabs={tabs} />;
    case 'wall':
      return <WallToy onExit={onExit} tabs={tabs} />;
    case 'balance':
      return <BalanceToy onExit={onExit} tabs={tabs} />;
    case 'puzzle':
      return <PuzzleToy onExit={onExit} tabs={tabs} />;
  }
}
